"use client";

import * as React from "react";
import { Star, Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  quote: "The stamped concrete patio they installed is the highlight of our backyard. It looks exactly like natural slate but with the durability of concrete. The team was professional and kept the site incredibly clean.",
  name: "Sarah Jenkins",
  location: "Carmel, IN",
  project: "Stamped Concrete Patio",
  rating: 5
}, {
  id: 2,
  quote: "We needed a heavy-duty floor for our new pole barn that could withstand heavy machinery. ConcreteCraft delivered a perfectly level, smooth finish that’s easy to clean and tough as nails.",
  name: "Mike Ross",
  location: "Noblesville, IN",
  project: "Pole Barn Floor",
  rating: 5
}, {
  id: 3,
  quote: "Our driveway was cracking and uneven, creating a hazard in winter. The team replaced it quickly, and the new broom finish provides excellent traction. Highly recommend their services.",
  name: "Emily White",
  location: "Fishers, IN",
  project: "Driveway Replacement",
  rating: 5
}, {
  id: 4,
  quote: "Professional, on time, and on budget. The decorative border on our walkway adds such a nice curb appeal boost. It's rare to find this level of craftsmanship these days.",
  name: "David Miller",
  location: "Zionsville, IN",
  project: "Decorative Walkway",
  rating: 5
}];
export default function StoriesFromTheSlab() {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  React.useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  const handleDotClick = (index: number) => {
    if (api) {
      api.scrollTo(index);
    }
  };
  return <section id="stories-from-the-slab" ref={ref} className={cn("bg-background py-20 transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="container mx-auto px-4">
        <div className="mb-12 text-center">
          <h2 className="text-primary mb-2 text-sm font-bold tracking-widest uppercase">
            Testimonials
          </h2>
          <h3 className="text-foreground font-serif text-3xl font-bold md:text-4xl">
            Stories from the Slab
          </h3>
        </div>

        <div className="mx-auto max-w-4xl">
          <Carousel setApi={setApi} className="w-full">
            <CarouselContent>
              {testimonials.map((testimonial, index) => <CarouselItem key={testimonial.id} data-index={index}>
                  <div className="p-2">
                    <Card className="bg-card border-border/50 shadow-lg relative overflow-hidden border">
                      <div className="bg-primary/5 absolute top-0 right-0 -mt-10 -mr-10 h-40 w-40 rounded-full blur-3xl" />
                      <div className="bg-primary/5 absolute bottom-0 left-0 -mb-10 -ml-10 h-40 w-40 rounded-full blur-3xl" />
                      
                      <CardContent className="relative flex flex-col items-center gap-8 p-8 text-center md:p-12">
                        {}
                        <div className="flex gap-1">
                          {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="text-primary size-6 fill-current" data-index={i} />)}
                        </div>

                        {}
                        <div className="relative">
                          <Quote className="text-muted-foreground/20 absolute -top-6 -left-6 size-10 -scale-x-100" />
                          <blockquote className="text-foreground font-serif text-xl italic leading-relaxed md:text-2xl">
                            &ldquo;{testimonial.quote}&rdquo;
                          </blockquote>
                          <Quote className="text-muted-foreground/20 absolute -bottom-6 -right-6 size-10" />
                        </div>

                        {}
                        <div className="flex flex-col items-center gap-3">
                          <div className="flex flex-col items-center">
                            <span className="text-foreground text-lg font-bold">
                              {testimonial.name}
                            </span>
                            <span className="text-muted-foreground text-sm">
                              {testimonial.location}
                            </span>
                          </div>
                          <Badge variant="secondary" className="bg-secondary/10 text-secondary-foreground hover:bg-secondary/20 mt-2 border-0 px-3 py-1">
                            {testimonial.project}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            {}
            <div className="hidden md:block">
              <CarouselPrevious className="-left-12 border-border bg-background hover:bg-accent hover:text-accent-foreground" />
              <CarouselNext className="-right-12 border-border bg-background hover:bg-accent hover:text-accent-foreground" />
            </div>

            {}
            <div className="mt-8 flex justify-center gap-2">
              {Array.from({
              length: count
            }).map((_, index) => <button key={index} data-index={index} onClick={() => handleDotClick(index)} className={cn("h-2.5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2", current === index ? "bg-primary w-8" : "bg-muted-foreground/30 hover:bg-muted-foreground/50 w-2.5")} aria-label={`Go to slide ${index + 1}`} />)}
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}